import { Component, EventEmitter, Input, OnInit, Output, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { NgbProgressbar } from "@ng-bootstrap/ng-bootstrap/progressbar";

import { ApiService } from "src/app/api.service";
import { DvsUtilities, ADMIN_OPERATIONS_LIMIT } from "src/app/dvs-utilities";
import { DatetimePipe, RuntimePipe } from "src/app/pipes";
import { ActionButtonComponent } from "src/app/shared/action-button/action-button.component";
import { DayPickerComponent } from "src/app/shared/day-picker/day-picker.component";
import { APIResults, AssemblyConfig } from "src/app/sharedinterfaces";
declare const BrandVars;

@Component({
	selector: "app-asset-destructive",
	templateUrl: "./asset-destructive.component.html",
	styleUrls: ["./asset-destructive.component.scss"],
	imports: [FormsModule, ActionButtonComponent, DayPickerComponent, NgbProgressbar, DatetimePipe, RuntimePipe]
})
export class AssetDestructiveComponent implements OnInit {
	private api = inject(ApiService);
	private router = inject(Router);
	readonly ADMIN_OPERATIONS_LIMIT = ADMIN_OPERATIONS_LIMIT;

	@Input()
	asset: APIResults.Asset;

	@Output()
	UpdateTree: EventEmitter<void> = new EventEmitter<void>();

	public brand = BrandVars;
	public GetOpClass = DvsUtilities.GetOpClass;

	public confirmOBCU: string;
	public confirmWipe: string;
	public confirmDelete: string;
	public resetAssetAuth: string;

	public assetOps: APIResults.Operation[] = null;
	public operationToDelete: APIResults.Operation = null;

	public componentList: (AssemblyConfig.Component | APIResults.VShaft)[] = null;
	public componentForAcq: AssemblyConfig.Component = null;
	public operationForAcq: APIResults.Operation = null;
	public dateForAcq: Date = null;
	public acquisitionList: APIResults.Acquisition[] = null;
	public acquisitionToDelete = null;

	public wipeStatus: string;
	public wipeProgress: number;

	public organizations: APIResults.Organization[] = null;
	public selectedOrganization: APIResults.Organization = null;

	async ngOnInit(): Promise<void> {
		await Promise.all([
			this.RefreshAssetOps(),
			this.RefreshAssetComponents(),
			this.RefreshOrgs(),
		]);
	}

	private async RefreshAssetOps() {
		this.assetOps = [];
		this.operationToDelete = null;
		this.assetOps = await this.api.GetOperations(this.asset.asset_id, ADMIN_OPERATIONS_LIMIT, true);
	}

	private async RefreshAssetComponents() {
		this.componentList = null;
		const components = await this.api.GetAllComponents(this.asset.asset_id);
		const vshafts = await this.api.GetAllVShafts(this.asset.asset_id);
		for (const vshaft of vshafts) {
			vshaft.id = vshaft.shftid;
		}
		this.componentList = [...components, ...vshafts];
	}

	private async RefreshOrgs() {
		this.organizations = null;
		this.organizations = await this.api.GetOrganizations();
	}

	public async ResetAuth() {
		if (this.confirmOBCU !== this.asset.obcu_serial_number) {
			throw new Error("Server ID confirmation does not match");
		}
		this.resetAssetAuth = undefined;
		const result = await this.api.ResetAsset(this.asset.asset_id);
		this.resetAssetAuth = result.auth;
	}

	public async ListAcquisitions() {
		this.acquisitionToDelete = null;
		this.acquisitionList = null;
		if (this.operationForAcq && this.componentForAcq) {
			this.acquisitionList = await this.api.GetOperationAcquisitions(this.operationForAcq.operation_id, this.componentForAcq.id);
		} else if (this.dateForAcq && this.componentForAcq) {
			this.acquisitionList = await this.api.GetOperationAcquisitions(this.assetOps[0].operation_id, this.componentForAcq.id, this.dateForAcq);
		}
	}

	public async DeleteAcquisition() {
		if (this.confirmOBCU !== this.asset.obcu_serial_number) {
			throw new Error("Server ID confirmation does not match");
		}
		await this.api.DeleteAcquisition(this.acquisitionToDelete.acquisition_id);
		await this.ListAcquisitions();
	}

	public async DeleteOperation() {
		if (this.confirmOBCU !== this.asset.obcu_serial_number) {
			throw new Error("Server ID confirmation does not match");
		}
		await this.api.DeleteOperation(this.asset.asset_id, this.operationToDelete.operation_id);
		await this.RefreshAssetOps();
	}

	public async WipeAsset() {
		if (this.confirmOBCU !== this.asset.obcu_serial_number) {
			throw new Error("Server ID confirmation does not match");
		}

		const result = await this.api.WipeAsset(this.asset.asset_id);
		const taskId = result.task;
		await this.api.PollTaskToCompletion(taskId, (status, progress) => {
			this.wipeStatus = status;
			this.wipeProgress = progress;
		});
	}

	public async DeleteAsset() {
		if (this.confirmOBCU !== this.asset.obcu_serial_number) {
			throw new Error("Server ID confirmation does not match");
		}
		await this.api.DeleteAsset(this.asset.asset_id);
		this.router.navigate(["/admin/assets/org", this.asset.organization_id, "fleet", this.asset.fleet_id ?? "null"]);
		this.UpdateTree.emit();
	}

	public async MoveAsset() {
		if (this.confirmOBCU !== this.asset.obcu_serial_number) {
			throw new Error("Server ID confirmation does not match");
		}
		if (!this.selectedOrganization) {
			throw new Error("No organization selected");
		}
		await this.api.UpdateAssetAdmin(this.asset.asset_id, { organization_id: this.selectedOrganization.organization_id, fleet_id: null });
		this.router.navigate(["/admin/assets/org", this.selectedOrganization.organization_id, "fleet", "null", "asset", this.asset.asset_id]);
		this.UpdateTree.emit();
		this.selectedOrganization = null;
	}
}
